import React, { useState } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Icon from 'components/AppIcon';

const GenerateQRModal = ({ onClose, onSubmit, productId }) => {
  const createCertificateField = () => ({
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    type: 'certificate',
    name: '',
    icon_asset_id: '',
    doc_asset_id: '',
    icon_url: '',
    doc_url: '',
    icon_uploading: false,
    doc_uploading: false,
    order: 1,
    enabled: true,
  });

  const [formData, setFormData] = useState({
    title: '',
    qr_no: 1,
    generate_pin: false,
    warning_limit: 1,
    ask_mobile: false,
    scan_mobile_only: false,
    ask_geo: false,
    pin: false,
    product_id: productId,
    show_ip: true,
    show_first_scan: true,

  });

  const [errors, setErrors] = useState({});
  const [enableCustomField, setEnableCustomField] = useState(false);
  const [useCustomNote, setUseCustomNote] = useState(false);
  const [custom_note, setCustomNote] = useState("");
  const [customFields, setCustomFields] = useState([
    createCertificateField()
  ]);
  const [loading, setLoading] = useState(false);
  const [pendingDeleteAssetIds, setPendingDeleteAssetIds] = useState([]);

  const token = localStorage.getItem('token');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.redirect_url && !/^https?:\/\/.+/.test(formData.redirect_url.trim())) {
      newErrors.redirect_url = 'Enter a valid URL starting with http:// or https://';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Batch name is required';
    }

    if (useCustomNote) {
      if (!custom_note.trim()) {
        newErrors.custom_note = "Note is required";
      }
    }

    if (enableCustomField) {
      const hasInvalidField = customFields.some(
        (field) => !field.name.trim() || !field.icon_asset_id || !field.doc_asset_id
      );
      if (hasInvalidField) {
        newErrors.custom_fields = 'Each custom field needs a name, icon, and file upload';
      }
    }

    setErrors(newErrors);
    console.log(newErrors)
    return Object.keys(newErrors).length === 0;
  };

  const uploadAsset = async (file, usage) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('usage', usage);

    const response = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/uploadassets`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: uploadFormData
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result?.message || 'Upload failed');
    }

    const assetId = result?.asset_id || result?.id || result?.data?.asset_id || result?.data?.id;
    const assetUrl = result?.url || result?.data?.url || '';
    if (!assetId) {
      throw new Error('Upload succeeded but no asset id was returned');
    }

    return { assetId, assetUrl };
  };

  const cleanupAssets = async (assetIds = []) => {
    if (!assetIds.length) return;
    await Promise.allSettled(
      assetIds.map((assetId) =>
        fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/assets/${assetId}?ifTemp=true`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
      )
    );
  };

  const handleClose = async () => {
    if (loading) return;
    await cleanupAssets(pendingDeleteAssetIds);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (validateForm()) {
      try {
        setLoading(true);
        const filteredCustomFields = customFields
          .filter((f) => f.name.trim() && f.icon_asset_id && f.doc_asset_id)
          .map((field, index) => ({
            id: field.id,
            type: 'certificate',
            name: field.name.trim(),
            icon_asset_id: field.icon_asset_id,
            doc_asset_id: field.doc_asset_id,
            order: index + 1,
            enabled: true
          }));

        const response = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/qrcode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            warning_limit: parseInt(formData.warning_limit),
            scan_mobile_only: formData.scan_mobile_only,
            ask_geo: formData.ask_geo,
            ask_mobile: formData.ask_mobile,
            pin: formData.generate_pin, // this controls whether to generate pin
            qr_no: parseInt(formData.qr_no),
            product_id: formData.product_id,
            title: formData.title,
            redirect_url: formData.redirect_url,
            youtube_url: formData.youtube_url,
            instagram_url: formData.instagram_url,
            meta_url: formData.meta_url,
            linkedIn_url: formData.linkedIn_url,
            x_url: formData.x_url,
            show_ip: formData.show_ip,
            show_first_scan: formData.show_first_scan,
            useCustomNote,
            custom_note: useCustomNote ? custom_note.trim() : '',
            custom_fields: enableCustomField ? filteredCustomFields : []
          }),
        });

        const result = await response.json();

        if (response.ok) {
          await cleanupAssets(pendingDeleteAssetIds);
          setPendingDeleteAssetIds([]);
          onSubmit(formData);
        } else {
          console.error(result.error || 'Failed to generate QR codes');
          alert('Failed to generate QR codes');
        }
      } catch (err) {
        console.error('Error submitting form:', err);
        alert('Something went wrong');
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleCustomFieldNameChange = (index, value) => {
    const updatedFields = [...customFields];
    updatedFields[index].name = value;
    setCustomFields(updatedFields);
    if (errors.custom_fields) {
      setErrors((prev) => ({ ...prev, custom_fields: '' }));
    }
  };

  const handleAssetUpload = async (index, file, kind) => {
    if (!file) return;
    const uploadingKey = kind === 'icon' ? 'icon_uploading' : 'doc_uploading';
    const idKey = kind === 'icon' ? 'icon_asset_id' : 'doc_asset_id';
    const urlKey = kind === 'icon' ? 'icon_url' : 'doc_url';

    setCustomFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, [uploadingKey]: true } : field))
    );

    try {
      const { assetId, assetUrl } = await uploadAsset(file, kind);
      setCustomFields((prev) =>
        prev.map((field, i) => {
          if (i !== index) return field;
          const oldAssetId = field[idKey];
          if (oldAssetId && oldAssetId !== assetId) {
            setPendingDeleteAssetIds((prevIds) => [...new Set([...prevIds, oldAssetId])]);
          }
          return { ...field, [idKey]: assetId, [urlKey]: assetUrl, [uploadingKey]: false };
        })
      );
      if (errors.custom_fields) {
        setErrors((prev) => ({ ...prev, custom_fields: '' }));
      }
    } catch (uploadError) {
      setCustomFields((prev) =>
        prev.map((field, i) => (i === index ? { ...field, [uploadingKey]: false } : field))
      );
      alert(uploadError.message || 'File upload failed');
    }
  };

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { ...createCertificateField(), order: prev.length + 1 }]);
  };

  const removeCustomField = (index) => {
    setCustomFields((prev) => {
      const field = prev[index];
      if (field?.icon_asset_id || field?.doc_asset_id) {
        setPendingDeleteAssetIds((ids) => [
          ...new Set([...ids, field.icon_asset_id, field.doc_asset_id].filter(Boolean))
        ]);
      }
      const updated = prev.filter((_, i) => i !== index);
      return updated.length ? updated : [createCertificateField()];
    });
  };



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-text-primary">Generate QR Code</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="#6b7280" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="p-6 max-h-[56vh] overflow-y-auto space-y-6">
          <div>
            {/* Batch Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch Name *
              </label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter Batch name"
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">

            <div className="space-y-4">
              {/* QR Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  No of QR Required *
                </label>
                <Input
                  type="number"
                  name="qr_no"
                  value={formData.qr_no}
                  onChange={handleInputChange}
                  placeholder="Enter number of QR codes"
                  className={errors.qr_no ? 'border-red-500' : ''}
                />
                {errors.qr_no && (
                  <p className="mt-1 text-sm text-red-600">{errors.qr_no}</p>
                )}
              </div>



              {/* Redirect Url  */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Redirect to
                </label>
                <Input
                  type="url"
                  name="redirect_url"
                  value={formData.redirect_url}
                  onChange={handleInputChange}
                  placeholder="Enter Redirection Url"
                />
                {errors.redirect_url && (
                  <p className="mt-1 text-sm text-red-600">{errors.redirect_url}</p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1' >
                  Meta Url
                </label>
                <Input
                  type="url"
                  name="meta_url"
                  value={formData.meta_url}
                  onChange={handleInputChange}
                  placeholder="Enter Facebook Page url"
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1' >
                  LinkedIn Url
                </label>
                <Input
                  type="url"
                  name="linkedIn_url"
                  value={formData.linkedIn_url}
                  onChange={handleInputChange}
                  placeholder="Enter LinkedIn url"
                />
              </div>

            </div>

            <div className='space-y-4'>
              {/* Warning Limit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warning Limit
                </label>
                <Input
                  type="number"
                  name="warning_limit"
                  value={formData.warning_limit}
                  onChange={handleInputChange}
                  placeholder="Enter warning limit"
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1' >
                  Youtube Channel Url
                </label>
                <Input
                  type="url"
                  name="youtube_url"
                  value={formData.youtube_url}
                  onChange={handleInputChange}
                  placeholder="Enter YouTube url"
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1' >
                  Instagram Url
                </label>
                <Input
                  type="url"
                  name="instagram_url"
                  value={formData.instagram_url}
                  onChange={handleInputChange}
                  placeholder="Enter Instagram url"
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1' >
                  X (formerly Twitter) Url
                </label>
                <Input
                  type="url"
                  name="x_url"
                  value={formData.x_url}
                  onChange={handleInputChange}
                  placeholder="Enter x url"
                />
              </div>
            </div>

            <div className='space-y-2'>
              {/* Generate PIN */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="generate_pin"
                  name="generate_pin"
                  className='rounded-full'
                  checked={formData.generate_pin}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, generate_pin: e.target.checked }))
                  }
                />
                <label htmlFor="generate_pin" className="text-sm font-medium text-gray-700">
                  Generate 4-digit PIN
                </label>
              </div>

              {/* Ask Mobile */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="ask_mobile"
                  name="ask_mobile"
                  checked={formData.ask_mobile}
                  className='rounded-full'
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, ask_mobile: e.target.checked }))
                  }
                />
                <label htmlFor="ask_mobile" className="text-sm font-medium text-gray-700">
                  Ask for Mobile Number on Scan
                </label>
              </div>

              {/* Show Ip Address */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="show_ip"
                  name="show_ip"
                  className='rounded-full'
                  checked={formData.show_ip}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, show_ip: e.target.checked }))
                  }
                />
                <label htmlFor="show_ip" className="text-sm font-medium text-gray-700">
                  Show User IP Address
                </label>
              </div>
            </div>


            <div className='space-y-2'>
              {/* Scan Mobile Only */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="scan_mobile_only"
                  name="scan_mobile_only"
                  className='rounded-full'
                  checked={formData.scan_mobile_only}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scan_mobile_only: e.target.checked }))
                  }
                />
                <label htmlFor="scan_mobile_only" className="text-sm font-medium text-gray-700">
                  Allow Scan from Mobile Devices Only
                </label>
              </div>

              {/* Ask Geolocation */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="ask_geo"
                  name="ask_geo"
                  checked={formData.ask_geo}
                  className='rounded-full'
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, ask_geo: e.target.checked }))
                  }
                />
                <label htmlFor="ask_geo" className="text-sm font-medium text-gray-700">
                  Ask for Geolocation
                </label>
              </div>

              {/* Show show first scan */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="show_first_scan"
                  name="show_first_scan"
                  className='rounded-full'
                  checked={formData.show_first_scan}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, show_first_scan: e.target.checked }))
                  }
                />
                <label htmlFor="show_first_scan" className="text-sm font-medium text-gray-700">
                  Show first scan
                </label>
              </div>
            </div>

          </div>

          {/* Use Custom Note */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="use_custom_note"
                className="text-sm font-medium text-gray-700"
              >
                Use Custom Note
              </label>

              <button
                type="button"
                onClick={() => {
                  setUseCustomNote((prev) => {
                    const next = !prev;
                    if (!next) {
                      setCustomNote('');
                      setErrors((prevErrors) => ({ ...prevErrors, custom_note: '' }));
                    }
                    return next;
                  });
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useCustomNote ? "bg-blue-600" : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useCustomNote ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </button>
            </div>
          </div>

          {
            useCustomNote &&
            <div>
              <label htmlFor="custom_note" className='block text-sm font-medium text-gray-700 mb-1'> Custom Notes </label>
              <Input
                type="text"
                name="custom_note"
                value={custom_note}
                className={errors.custom_note ? 'border-red-500' : ''}
                onChange={(e) => {
                  setCustomNote(e.target.value);
                  if (errors.custom_note) {
                    setErrors((prev) => ({ ...prev, custom_note: '' }));
                  }
                }} />
              {errors.custom_note &&
                <span className='flex items-center gap-2 mt-1 text-sm text-red-600'>
                  <Icon name="AlertCircle" size={14} /> {errors.custom_note}
                </span>
              }
            </div>
          }


          {/* Show Custom Fields */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="enable_custom_field"
                className="text-sm font-medium text-gray-700"
              >
                Enable Custom Field
              </label>

              <button
                type="button"
                onClick={() => {
                  setEnableCustomField(!enableCustomField);
                  if (enableCustomField) {
                    setCustomFields((prev) => {
                      const idsToDelete = prev.flatMap((field) => [field.icon_asset_id, field.doc_asset_id]).filter(Boolean);
                      if (idsToDelete.length) {
                        setPendingDeleteAssetIds((ids) => [...new Set([...ids, ...idsToDelete])]);
                      }
                      return [createCertificateField()];
                    });
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enableCustomField ? "bg-blue-600" : "bg-gray-300"
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableCustomField ? "translate-x-6" : "translate-x-1"
                    }`}
                />
              </button>
            </div>
          </div>


          {errors.custom_fields && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <Icon name="AlertCircle" size={14} /> {errors.custom_fields}
            </p>
          )}

          {enableCustomField && (
            <div className="mt-4 space-y-4">
              {customFields.map((field, index) => (
                <div key={index} className="relative p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-4 transition-all hover:border-blue-200">
                  {/* Header with Remove Action */}
                  <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                      Field #{index + 1}
                    </span>
                    {customFields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCustomField(index)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </div>

                  {/* Field Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                    <Input
                      type="text"
                      value={field.name || ''}
                      onChange={(e) => handleCustomFieldNameChange(index, e.target.value)}
                      placeholder="e.g. Download Certificate"
                      className="bg-gray-50/50"
                    />
                  </div>

                  {/* Upload Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Icon Upload */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-600">Icon Image</label>
                      <div className={`relative flex items-center justify-center border-2 border-dashed rounded-lg p-2 transition-colors ${field.icon_asset_id ? 'border-green-200 bg-green-50/30' : 'border-gray-200 hover:border-blue-300'}`}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleAssetUpload(index, e.target.files?.[0], 'icon')}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="text-center">
                          {field.icon_uploading ? (
                            <span className="text-[10px] text-blue-500 animate-pulse">Uploading...</span>
                          ) : field.icon_asset_id ? (
                            <span className="text-[10px] text-green-600 flex items-center gap-1"><Icon name="Check" size={12} /> Icon Ready</span>
                          ) : (
                            <span className="text-[10px] text-gray-500">Click to upload icon</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Doc Upload */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-600">Attachment (PDF/DOC)</label>
                      <div className={`relative flex items-center justify-center border-2 border-dashed rounded-lg p-2 transition-colors ${field.doc_asset_id ? 'border-green-200 bg-green-50/30' : 'border-gray-200 hover:border-blue-300'}`}>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={(e) => handleAssetUpload(index, e.target.files?.[0], 'doc')}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="text-center">
                          {field.doc_uploading ? (
                            <span className="text-[10px] text-blue-500 animate-pulse">Uploading...</span>
                          ) : field.doc_asset_id ? (
                            <span className="text-[10px] text-green-600 flex items-center gap-1"><Icon name="Check" size={12} /> File Ready</span>
                          ) : (
                            <span className="text-[10px] text-gray-500">Click to upload file</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addCustomField}
                className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-200 transition-all"
              >
                <Icon name="Plus" size={16} /> Add Another Attachment
              </button>
            </div>)}


        </form>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading} variant="primary" className="text-white" onClick={handleSubmit}>
            Generate QR Code
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GenerateQRModal;
